import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Loader2, XCircle } from "lucide-react";
import api, { fmtDate, money2 } from "@/lib/api";

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "processing" });

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "failed" });
      return;
    }
    let ticks = 0;
    const timer = setInterval(async () => {
      ticks += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.status === "conflict") {
          clearInterval(timer);
          setState({ phase: "conflict" });
        } else if (data.payment_status === "paid" && data.type === "damage_fee") {
          clearInterval(timer);
          setState({ phase: "fee_paid", ...data });
        } else if (data.payment_status === "paid" && data.booking_reference) {
          clearInterval(timer);
          setState({ phase: "confirmed", ...data });
        } else if (data.payment_status === "failed" || data.payment_status === "expired") {
          clearInterval(timer);
          setState({ phase: "failed" });
        } else if (ticks > 15) {
          clearInterval(timer);
          setState({ phase: "timeout" });
        }
      } catch {
        if (ticks > 15) {
          clearInterval(timer);
          setState({ phase: "failed" });
        }
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [sessionId]);

  return (
    <main className="grid min-h-[calc(100vh-5rem)] place-items-center bg-paper px-4 py-20" data-testid="payment-success-page">
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-10 text-center shadow-xl shadow-slate-900/5"
      >
        {state.phase === "processing" && (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-royal" />
            <h1 className="mt-5 font-display text-2xl font-bold text-ink">Confirming your payment…</h1>
            <p className="mt-2 text-sm text-slate-500" data-testid="payment-status-text">
              Stripe confirmed your card — we're locking in your reservation.
            </p>
          </>
        )}
        {state.phase === "confirmed" && (
          <>
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 14 }}
              className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-emerald-50"
            >
              <CheckCircle2 className="h-8 w-8 text-emerald-500" />
            </motion.div>
            <h1 className="mt-5 font-display text-2xl font-bold text-ink">Reservation locked in.</h1>
            <p className="mt-2 text-sm text-slate-600">
              {state.listing_title} · {fmtDate(state.start_date)} – {fmtDate(state.end_date)}
            </p>
            <p className="mt-1 text-sm font-semibold text-ink">
              Deposit paid {money2(state.amount_reserved)} · {money2(state.balance_due)} due on delivery
            </p>
            <p className="mt-4 inline-block rounded-full bg-royal-light px-5 py-2 font-mono text-sm font-semibold tracking-[0.2em] text-royal" data-testid="payment-reference">
              {state.booking_reference}
            </p>
            <p className="mt-4 text-xs text-slate-500">
              A confirmation email is on its way. We'll text your delivery window the evening before drop-off.
            </p>
            <Link
              to="/account"
              data-testid="view-bookings-button"
              className="mt-6 inline-flex items-center gap-2 rounded-xl bg-ink px-6 py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-royal"
            >
              View my bookings <ArrowRight className="h-4 w-4" />
            </Link>
          </>
        )}
        {state.phase === "fee_paid" && (
          <>
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 14 }}
              className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-emerald-50"
            >
              <CheckCircle2 className="h-8 w-8 text-emerald-500" />
            </motion.div>
            <h1 className="mt-5 font-display text-2xl font-bold text-ink">Damage fee paid.</h1>
            <p className="mt-2 text-sm text-slate-600" data-testid="fee-paid-amount">
              {money2(state.amount)} received — thank you for settling the return inspection fees.
            </p>
            <p className="mt-4 text-xs text-slate-500">
              A receipt is on its way to your email. Questions? Call (541) 408-7019.
            </p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 rounded-xl bg-ink px-6 py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-royal"
            >
              Back home <ArrowRight className="h-4 w-4" />
            </Link>
          </>
        )}
        {(state.phase === "failed" || state.phase === "timeout" || state.phase === "conflict") && (
          <>
            <XCircle className="mx-auto h-10 w-10 text-red-500" />
            <h1 className="mt-5 font-display text-2xl font-bold text-ink">
              {state.phase === "conflict" ? "Those dates just got taken." : "Payment didn't complete."}
            </h1>
            <p className="mt-2 text-sm text-slate-500" data-testid="payment-status-text">
              {state.phase === "conflict"
                ? "Your payment was captured but the dates filled up — email reservations@oregon-tote-rentals.com and we'll sort a refund or new window."
                : state.phase === "timeout"
                  ? "Stripe is still confirming — check your account page in a minute."
                  : "No charge was made. Pick your dates and try again."}
            </p>
            <Link
              to="/catalog"
              data-testid="payment-retry-link"
              className="mt-6 inline-flex items-center gap-2 rounded-xl bg-royal px-6 py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-royal-dark"
            >
              Back to packages <ArrowRight className="h-4 w-4" />
            </Link>
          </>
        )}
      </motion.div>
    </main>
  );
}
